import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { MoreVertical, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  useDeleteAnnualReportingMutation,
  useGetAnnualReportingList,
} from "@/services";
import { toast } from "sonner";
import moment from "moment";

const useAnnualReporting = (selectedTab?: string) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onAdd = (tab: string) => {
    navigate(`add`, { state: { applicableCompany: tab } });
  };

  const { data: annualReportingList, isLoading: isAnnualReportingLoading } =
    useGetAnnualReportingList({applicableCompany: selectedTab?.toUpperCase(),});

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteAnnualReportingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Annual Reporting Deleted Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({ queryKey: ["annual-reporting"] });
        setOpenDialog(false);
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (row: any) => (
        <b className="text-primary font-[600]">
          {String(row.row.original.name || "")}
        </b>
      ),
    },
    {
      accessorKey: "updatedAt",
      header: "Uploaded Date",
      accessorFn: (row: any) =>
        row.updatedAt ? moment(row.updatedAt).format("MM-DD-YYYY") : "",
    },
    {
      accessorKey: "attachmentData",
      header: "Attachment",
      cell: (row: any) => (
        <u className="text-primary">
          {row.row.original?.attachmentData
            ? row.row.original?.attachmentData?.fileName
            : ""}
        </u>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDeletedRow(row.row.original);
                setOpenDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onDeleteConfirm = () => {
    deleteInfo({ id: deletedRow?._id });
  };

  return {
    onAdd,
    columns,
    openDialog,
    deletedRow,
    setOpenDialog,
    isDeletePending,
    onDeleteConfirm,
    annualReportingList,
    isAnnualReportingLoading,
  };
};

export default useAnnualReporting;
